import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Input } from "@/modules/shared/ui/components/input"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { FaFlag } from "react-icons/fa"
import type {
  EmployeeOnboardingDetailsDto,
  EmployeeFieldFlagDto,
} from "@/modules/employee/employee/dto/employeeDetails"
import {
  addressSchema,
  type AddressFormData,
} from "@/modules/employee/employee/schemas/employeeFormSchemas"

interface AddressDetailsFormProps {
  formData: EmployeeOnboardingDetailsDto["address"]
  onUpdate: (address: EmployeeOnboardingDetailsDto["address"]) => void
  isReadOnly?: boolean
  fieldFlags?: EmployeeFieldFlagDto[]
}

export function AddressDetailsForm({
  formData,
  onUpdate,
  isReadOnly = false,
  fieldFlags,
}: AddressDetailsFormProps) {
  const address = useMemo(
    () =>
      typeof formData === "string"
        ? {
            line1: "",
            line2: "",
            city: "",
            state: "",
            postalCode: "",
            country: "",
            complexNumber: "",
            complexName: "",
          }
        : formData,
    [formData],
  )

  const form = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      line1: address.line1,
      line2: address.line2 || "",
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      complexNumber: address.complexNumber || "",
      complexName: address.complexName || "",
    },
    mode: "onChange",
  })

  useEffect(() => {
    const subscription = form.watch((values) => {
      onUpdate(values as EmployeeOnboardingDetailsDto["address"])
    })
    return () => subscription.unsubscribe()
  }, [form, form.watch, onUpdate])

  // Sync external formData into form when it changes from outside (e.g. API load).
  // Coerce nullish → "" on both sides so the null-vs-empty-string mismatch from the
  // API doesn't loop reset → watch → onUpdate.
  useEffect(() => {
    const s = (v: unknown) => (v ?? "") as string
    const current = form.getValues()
    const hasExternalChange =
      s(current.line1) !== s(address.line1) ||
      s(current.line2) !== s(address.line2) ||
      s(current.city) !== s(address.city) ||
      s(current.state) !== s(address.state) ||
      s(current.postalCode) !== s(address.postalCode) ||
      s(current.country) !== s(address.country) ||
      s(current.complexNumber) !== s(address.complexNumber) ||
      s(current.complexName) !== s(address.complexName)

    if (!hasExternalChange) return

    form.reset({
      line1: s(address.line1),
      line2: s(address.line2),
      city: s(address.city),
      state: s(address.state),
      postalCode: s(address.postalCode),
      country: s(address.country),
      complexNumber: s(address.complexNumber),
      complexName: s(address.complexName),
    })
    form.trigger()
  }, [address, form])

  const sectionFlag = fieldFlags?.find(
    (f) => f.fieldName === "address" && !f.resolvedAt,
  )
  const isSectionFlagged = !!sectionFlag
  const isFieldDisabled = isReadOnly && !isSectionFlagged

  return (
    <Form {...form}>
      <form className="space-y-3">
        <h2 className="text-lg font-bold text-foreground">Address Details</h2>

        {isSectionFlagged && (
          <div className="flex items-start gap-2 text-sm text-orange-600 bg-orange-50 p-3 rounded-md border border-orange-200">
            <FaFlag className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium mb-1">
                This section needs to be updated:
              </p>
              <p className="text-orange-700">{sectionFlag.note}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="line1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Street Address *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="city"
            render={({ field }) => (
              <FormItem>
                <FormLabel>City *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Province *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="postalCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Postal Code *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    onChange={(e) =>
                      field.onChange(e.target.value.replace(/\D/g, ""))
                    }
                    disabled={isFieldDisabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="complexName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Complex / Estate Name (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isFieldDisabled}
                    placeholder="e.g. Sandton City Office Park"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="complexNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unit / Complex Number (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    disabled={isFieldDisabled}
                    placeholder="e.g. Unit 4B"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem className="sm:col-span-2">
                <FormLabel>Country *</FormLabel>
                <FormControl>
                  <Input {...field} disabled={isFieldDisabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  )
}
